import { BrowserCacheLocation, EventType, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { MsalAuthenticationTemplate, MsalProvider, useAccount, useMsal } from '@azure/msal-react';
import { CogniteClient } from '@cognite/sdk';
import { useQuery } from '@tanstack/react-query';

import { LabeledLoader } from '#core/components/layout/LabeledLoader.tsx';
import { env } from '#core/lib/utils/env.ts';

import { CogniteClientContext } from './CogniteClientContext.ts';
import { createAccessTokenAcquirer } from './createAccessTokenAcquirer.ts';

// TODO: move to schema/env.ts
// Single source of truth for every value derived from the MSAL `env.auth`
// config. The `PublicClientApplication` singleton and the auth request (login
// + token acquisition) both consume these, so the scopes used to obtain a token
// can never drift from the scopes the audience expects.
const { cluster, clientId, project, tenantId, redirectUri } = env.auth ?? ({} as NonNullable<typeof env.auth>);
const baseUrl = `https://${cluster}.cognitedata.com`;
const authority = `https://login.microsoftonline.com/${tenantId}`;
const scopes = [`${baseUrl}/.default`];
const request = { scopes };

// MSAL is built around a single long-lived client: its token cache, redirect
// handling, and event system all assume one stable instance. Create it once at
// module scope (never per render) so reloads reuse the same cache.
const pca = env.auth
  ? new PublicClientApplication({
      auth: {
        clientId,
        authority,
        redirectUri,
      },
      cache: {
        cacheLocation: BrowserCacheLocation.SessionStorage,
      },
    })
  : null;

pca?.addEventCallback((event) => {
  if (
    event.eventType === EventType.LOGIN_SUCCESS &&
    event?.payload &&
    'account' in event.payload &&
    event.payload?.account
  ) {
    console.debug('Login successful, setting active account:', event.payload.account);
    pca?.setActiveAccount(event.payload.account);
  }
});

export function MsalAppAuthProvider({ children }: React.PropsWithChildren) {
  if (!pca) {
    return children;
  }

  return (
    <MsalProvider instance={pca}>
      <MsalAuthenticationTemplate authenticationRequest={request} interactionType={InteractionType.Redirect}>
        <CogniteClientProvider>{children}</CogniteClientProvider>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}

export function CogniteClientProvider({ children }: React.PropsWithChildren) {
  const { accounts, instance } = useMsal();
  const account = useAccount(accounts.find((account) => account.tenantId === tenantId) ?? {});

  const { data: authenticatedClient } = useQuery({
    queryKey: ['cogniteAuth', account] as const,
    // Surface auth failures to the error boundary instead of stalling on the
    // loader forever; retry once to ride out a transient network hiccup.
    throwOnError: true,
    retry: 1,
    queryFn: async ({ queryKey: [, account] }) => {
      if (!account) {
        return null;
      }

      const client = new CogniteClient({
        appId: clientId ?? '',
        baseUrl: env.cdf?.url ?? baseUrl,
        project: project ?? '',
        oidcTokenProvider: createAccessTokenAcquirer(instance, account, request),
      });
      await client.authenticate();
      return client;
    },
  });

  if (!authenticatedClient) {
    return <LabeledLoader label="Authenticating..." />;
  }

  return <CogniteClientContext value={authenticatedClient}>{children}</CogniteClientContext>;
}
